import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { aiVetAPI, petAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const AIVet = () => {
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState('');
  const [chatTitle, setChatTitle] = useState('');
  const [showDebug, setShowDebug] = useState(true); // Debug panel
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: sessions, isLoading: sessionsLoading, error: sessionsError } = useQuery(
    'aiVetSessions',
    aiVetAPI.getSessions,
    {
      onError: (error) => {
        console.error('❌ Sessions query error:', error);
        console.error('Status:', error.response?.status);
        console.error('Data:', error.response?.data);
      }
    }
  );

  const { data: pets, error: petsError } = useQuery('pets', petAPI.getPets, {
    onError: (error) => {
      console.error('❌ Pets query error:', error);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);
    }
  });

  const createSessionMutation = useMutation(aiVetAPI.createSession, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('aiVetSessions');
      setShowNewChatModal(false);
      setSelectedPet('');
      setChatTitle('');
      navigate(`/app/virtual-vet/chat/${data.data.id}`);
    },
    onError: (error) => {
      console.error('Failed to create chat session:', error);
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        // The interceptor will handle the redirect, but we'll show a user-friendly message
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Your session has expired. Please sign in again with Google.', {
            duration: 5000
          });
        });
        setShowNewChatModal(false);
        // Clear local storage to force fresh login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Small delay before redirect to show the toast
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      } else {
        // For other errors, show a generic message and keep the modal open
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Failed to create chat session. Please try again.');
        });
      }
    }
  });

  const deleteSessionMutation = useMutation(aiVetAPI.deleteSession, {
    onSuccess: () => {
      queryClient.invalidateQueries('aiVetSessions');
    }
  });

  const handleCreateSession = (e) => {
    e.preventDefault();
    if (!selectedPet) return;

    const petName = pets?.data?.find(p => p.id === parseInt(selectedPet))?.name;
    const title = chatTitle.trim() || `Chat about ${petName}`;

    createSessionMutation.mutate({
      pet_id: parseInt(selectedPet),
      title
    });
  };

  const handleDeleteSession = (sessionId) => {
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      deleteSessionMutation.mutate(sessionId);
    }
  };

  if (sessionsLoading) {
    return <LoadingSpinner text="Loading AI Vet..." />;
  }

  const sessionsData = sessions?.data || [];
  const petsData = pets?.data || [];

  // Debug info
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-x-hidden">
      {/* Debug Panel */}
      {showDebug && (
        <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-blue-900">🔍 Debug Info</h3>
            <button
              onClick={() => setShowDebug(false)}
              className="text-blue-600 text-sm hover:text-blue-800"
            >
              Hide
            </button>
          </div>
          <div className="text-sm space-y-1">
            <p><strong>Token:</strong> {token ? `✅ ${token.substring(0, 30)}...` : '❌ NO TOKEN'}</p>
            <p><strong>User:</strong> {user ? `✅ ${JSON.parse(user).email}` : '❌ NO USER'}</p>
            <p><strong>Sessions Query:</strong> {
              sessionsLoading ? '⏳ Loading...' :
              sessionsError ? `❌ Error ${sessionsError.response?.status}` :
              sessions ? `✅ ${sessions.data?.length || 0} sessions` : '⏳ Waiting'
            }</p>
            <p><strong>Pets Query:</strong> {
              petsError ? `❌ Error ${petsError.response?.status}` :
              pets ? `✅ ${pets.data?.length || 0} pets` : '⏳ Loading'
            }</p>
            <p><strong>Path:</strong> {window.location.pathname}</p>
            <button
              onClick={() => {
                console.log('🔍 TEST BUTTON CLICKED - No API calls, just logging');
                alert(`Token exists: ${!!token}\nUser exists: ${!!user}\nSessions: ${sessions?.data?.length || 0}\nPets: ${pets?.data?.length || 0}`);
              }}
              className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
            >
              Test Button (Safe - No API calls)
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <SparklesIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mr-2 flex-shrink-0" />
            <span className="truncate">AI Veterinary Assistant</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Chat with our AI assistant for pet health guidance and advice</p>
        </div>
        <button
          onClick={() => setShowNewChatModal(true)}
          className="btn btn-primary flex items-center justify-center flex-shrink-0"
          disabled={petsData.length === 0}
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          New Chat
        </button>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Important Disclaimer</h3>
            <p className="mt-1 text-sm text-yellow-700">
              This AI assistant provides general information and guidance only. It is not a substitute for professional veterinary care. 
              For emergencies or serious health concerns, please contact your veterinarian immediately.
            </p>
          </div>
        </div>
      </div>

      {petsData.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No pets registered</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add a pet to your profile to start chatting with the AI vet.
          </p>
          <div className="mt-6">
            <Link to="/app/pets" className="btn btn-primary">
              Add Pet
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Recent Chat Sessions */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Recent Chats</h2>
              {sessionsData.length > 0 && (
                <Link to="/app/virtual-vet/sessions" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  View All
                </Link>
              )}
            </div>

            {sessionsData.length === 0 ? (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No chat sessions yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start your first conversation with the AI vet assistant.
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => setShowNewChatModal(true)}
                    className="btn btn-primary"
                  >
                    Start First Chat
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {sessionsData.slice(0, 5).map((session) => (
                  <div key={session.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{session.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
                          <span className="truncate">{session.pet_name} ({session.pet_type})</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="whitespace-nowrap">{session.message_count} messages</span>
                          {session.last_message_at && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span className="whitespace-nowrap">{new Date(session.last_message_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0 sm:ml-auto">
                      <Link
                        to={`/app/virtual-vet/chat/${session.id}`}
                        className="px-3 py-1 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700"
                      >
                        Continue
                      </Link>
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        className="p-1 text-red-600 hover:text-red-700"
                        title="Delete session"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">How the AI Vet Can Help</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 border border-gray-200 rounded-lg">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">Health Questions</h3>
                <p className="text-sm text-gray-500">Ask about symptoms, behaviors, or general health concerns</p>
              </div>
              
              <div className="p-4 border border-gray-200 rounded-lg">
                <ClockIcon className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="font-medium text-gray-900">Care Guidance</h3>
                <p className="text-sm text-gray-500">Get advice on feeding, exercise, and daily care routines</p>
              </div>

              <div className="p-4 border border-gray-200 rounded-lg">
                <SparklesIcon className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">Behavior Help</h3>
                <p className="text-sm text-gray-500">Understand your pet's behavior and training needs</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Start New Chat</h3>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Pet
                </label>
                <select
                  value={selectedPet}
                  onChange={(e) => setSelectedPet(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Choose a pet...</option>
                  {petsData.map((pet) => (
                    <option key={pet.id} value={pet.id}>
                      {pet.name} ({pet.pet_type})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chat Title (Optional)
                </label>
                <input
                  type="text"
                  value={chatTitle}
                  onChange={(e) => setChatTitle(e.target.value)}
                  placeholder="e.g., Check-up questions for Max"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewChatModal(false);
                    setSelectedPet('');
                    setChatTitle('');
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedPet || createSessionMutation.isLoading}
                  className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {createSessionMutation.isLoading ? 'Creating...' : 'Start Chat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIVet;